/**
 * Campaign → To do: one-to-one outreach, one person at a time (Alex,
 * 4 Nov 2026; backend services/campaignOutreach.js, migration 235).
 *
 * Claude saves people here with the reason to contact them and drafted
 * messages (mm_campaign_outreach); people added by hand appear too, with
 * empty drafts to write. For each person: get the email / mobile (credits),
 * open the email in the user's own Gmail / Outlook / mail app, copy the
 * LinkedIn note and open their profile, call — then mark what happened
 * (contacted / replied / meeting booked / not interested) or set a
 * follow-up date. Status changes move the person to the matching pipeline
 * stage. Nothing is sent by MarketMapr itself.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type Status = 'to_do' | 'contacted' | 'replied' | 'meeting' | 'not_interested';
interface LogEntry { at: string; action: string; channel?: string | null; by?: string; note?: string | null }
interface Card {
  id: number;
  entity_type: 'contact' | 'candidate' | 'company';
  entity_id: number;
  entity_name: string | null;
  entity_title: string | null;
  entity_company: string | null;
  entity_location: string | null;
  entity_headline: string | null;
  entity_email: string | null;
  entity_personal_email: string | null;
  entity_phone: string | null;
  entity_linkedin: string | null;
  outreach_reason: string | null;
  outreach_about: string | null;
  email_subject: string | null;
  email_body: string | null;
  linkedin_note: string | null;
  call_opener: string | null;
  outreach_status: Status;
  outreach_due: string | null;
  outreach_log: LogEntry[];
  outreach_source: string | null;
  outreach_evidence?: Evidence[] | null;
  signal_type?: string | null;
  signal_metadata?: Record<string, any> | null;
  signal_at?: string | null;
  stage_name: string | null;
}
interface Evidence { fact: string; date?: string | null; url?: string | null; source?: string | null }

// Everything behind the reason: Claude's evidence, plus the monitoring move
// a person was added from ("Add from monitoring"), so nobody has to go back
// to the chat to check what they're about to send.
function evidenceOf(c: Card): Evidence[] {
  const out: Evidence[] = [...(c.outreach_evidence || [])];
  const m = c.signal_metadata;
  if (m && (m.subject_name || m.from_company || m.to_company)) {
    const who = m.subject_name || 'Someone';
    const fact = m.direction === 'backfill'
      ? `${who}${m.subject_old_title ? ` (${m.subject_old_title})` : ''} left ${m.from_company || 'the company'}${m.to_company ? ` for ${m.to_company}` : ''}`
      : `${who} joined ${m.to_company || m.target_company || 'the company'}${m.subject_new_title ? ` as ${m.subject_new_title}` : ''}${m.from_company ? `, from ${m.from_company}` : ''}`;
    const when = m.move_date || c.signal_at;
    out.push({ fact, date: when ? new Date(when).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : null, source: 'MarketMapr monitoring' });
    if (m.changes_summary) out.push({ fact: String(m.changes_summary), source: 'MarketMapr monitoring' });
  }
  return out;
}
const hostOf = (u: string) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return 'link'; } };

const STATUS: Record<Status, { label: string; pill: string; dot: string }> = {
  to_do: { label: 'To do', pill: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  contacted: { label: 'Contacted', pill: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
  replied: { label: 'Replied', pill: 'bg-teal-50 text-teal-700', dot: 'bg-teal-500' },
  meeting: { label: 'Meeting booked', pill: 'bg-violet-50 text-violet-700', dot: 'bg-violet-500' },
  not_interested: { label: 'Not interested', pill: 'bg-gray-100 text-gray-500', dot: 'bg-gray-300' },
};

type Filter = 'due' | 'later' | 'contacted' | 'replied' | 'closed' | 'all';
const FILTERS: { key: Filter; label: string }[] = [
  { key: 'due', label: 'Due now' },
  { key: 'later', label: 'Follow up later' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'replied', label: 'Replied & meetings' },
  { key: 'closed', label: 'Not interested' },
  { key: 'all', label: 'Everyone' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const dueDay = (c: Card) => (c.outreach_due ? String(c.outreach_due).slice(0, 10) : null);
const isLater = (c: Card) => { const d = dueDay(c); return !!d && d > todayISO(); };
const inFilter = (c: Card, f: Filter) => {
  switch (f) {
    case 'due': return c.outreach_status === 'to_do' ? !isLater(c) : (c.outreach_status === 'contacted' && !!dueDay(c) && !isLater(c));
    case 'later': return isLater(c) && c.outreach_status !== 'not_interested';
    case 'contacted': return c.outreach_status === 'contacted';
    case 'replied': return c.outreach_status === 'replied' || c.outreach_status === 'meeting';
    case 'closed': return c.outreach_status === 'not_interested';
    default: return true;
  }
};
const initials = (n: string | null) => (n || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
const fmtDay = (iso: string) => new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const plusDays = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const firstName = (n: string | null) => (n || '').split(/\s+/)[0] || '';

const ACTION_LABEL: Record<string, string> = {
  drafted: 'Drafts saved', emailed: 'Email opened to send', linkedin: 'LinkedIn note copied', called: 'Called',
  status: 'Status changed', snoozed: 'Follow-up set', edited: 'Drafts edited',
};

interface Props { campaignId: string; token: string | null; onChanged?: () => void }

const CampaignOutreach: React.FC<Props> = ({ campaignId, token, onChanged }) => {
  const [cards, setCards] = useState<Card[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('due');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/campaigns/${campaignId}/outreach`, { headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load');
      setCards(d.cards || []);
      setError(null);
      return d.cards as Card[];
    } catch (e: any) { setError(e.message || 'Could not load'); return null; }
  }, [campaignId, headers]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const out: Record<Filter, number> = { due: 0, later: 0, contacted: 0, replied: 0, closed: 0, all: 0 };
    (cards || []).forEach((c) => FILTERS.forEach((f) => { if (inFilter(c, f.key)) out[f.key] += 1; }));
    return out;
  }, [cards]);
  const shown = useMemo(() => (cards || []).filter((c) => inFilter(c, filter)), [cards, filter]);

  // Keep a sensible selection as the list changes.
  useEffect(() => {
    if (!cards) return;
    if (!selectedId || !shown.some((c) => c.id === selectedId)) setSelectedId(shown[0]?.id ?? null);
  }, [cards, shown, selectedId]);
  // Start on "Everyone" when nothing is due, so the first view isn't empty.
  const firstLoad = useRef(true);
  useEffect(() => {
    if (cards && firstLoad.current) {
      firstLoad.current = false;
      if (!cards.some((c) => inFilter(c, 'due')) && cards.length) setFilter('all');
    }
  }, [cards]);

  const selected = (cards || []).find((c) => c.id === selectedId) || null;

  const patch = useCallback(async (card: Card, body: Record<string, any>) => {
    // Optimistic: the list updates at once; reload to pick up stage moves.
    setCards((cs) => (cs || []).map((c) => (c.id === card.id ? {
      ...c,
      ...(body.status ? { outreach_status: body.status } : {}),
      ...('due' in body ? { outreach_due: body.due } : {}),
      ...('emailSubject' in body ? { email_subject: body.emailSubject } : {}),
      ...('emailBody' in body ? { email_body: body.emailBody } : {}),
      ...('linkedinNote' in body ? { linkedin_note: body.linkedinNote } : {}),
      ...('callOpener' in body ? { call_opener: body.callOpener } : {}),
      ...(body.log ? { outreach_log: [...(c.outreach_log || []), { at: new Date().toISOString(), ...body.log, by: 'You' }] } : {}),
    } : c)));
    const r = await fetch(`/api/campaigns/${campaignId}/cards/${card.id}/outreach`, { method: 'PATCH', headers, body: JSON.stringify(body) });
    if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.error || 'Could not save'); }
    if (body.status) onChanged?.();
  }, [campaignId, headers, onChanged]);

  const goNext = () => {
    const i = shown.findIndex((c) => c.id === selectedId);
    const next = shown[i + 1] || shown[0];
    if (next) setSelectedId(next.id);
  };

  if (error && !cards) return <div className="card p-8 text-center text-sm text-red-600">{error}</div>;
  if (!cards) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" /></div>;
  if (!cards.length) return <EmptyState />;

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => { setFilter(f.key); setMobileDetail(false); }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === f.key ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'}`}>
            {f.label}
            <span className={`ml-1.5 tabular-nums ${filter === f.key ? 'text-gray-300' : 'text-gray-400'}`}>{counts[f.key]}</span>
          </button>
        ))}
      </div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-5 items-start">
        {/* Queue */}
        <div className={`card overflow-hidden ${mobileDetail ? 'hidden lg:block' : ''}`}>
          {shown.length === 0 ? (
            <p className="p-6 text-sm text-gray-500 text-center">{filter === 'due' ? 'Nothing due right now. Nice work.' : 'Nobody here.'}</p>
          ) : (
            <ul className="divide-y divide-gray-100 lg:max-h-[calc(100vh-260px)] lg:overflow-y-auto">
              {shown.map((c) => {
                const active = c.id === selectedId;
                const d = dueDay(c);
                return (
                  <li key={c.id}>
                    <button onClick={() => { setSelectedId(c.id); setMobileDetail(true); }}
                      className={`w-full text-left px-4 py-3.5 flex gap-3 transition-colors ${active ? 'bg-blue-50/70' : 'hover:bg-gray-50'}`}>
                      <span className={`mt-0.5 w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-semibold ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{initials(c.entity_name)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="font-medium text-gray-900 truncate">{c.entity_name || 'Unknown'}</span>
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS[c.outreach_status]?.dot || 'bg-gray-300'}`} title={STATUS[c.outreach_status]?.label} />
                        </span>
                        <span className="block text-xs text-gray-500 truncate">{[c.entity_title, c.entity_company].filter(Boolean).join(' · ')}</span>
                        {(c.outreach_about || c.outreach_reason) && (
                          <span className="block text-xs text-gray-700 mt-1 line-clamp-2">{c.outreach_about || c.outreach_reason}</span>
                        )}
                        {evidenceOf(c).length > 0 && (
                          <span className="inline-flex items-center gap-1 mt-1.5 mr-2 text-[11px] font-medium text-emerald-700">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                            {evidenceOf(c).length} fact{evidenceOf(c).length === 1 ? '' : 's'} behind it
                          </span>
                        )}
                        {d && c.outreach_status !== 'not_interested' && (
                          <span className={`inline-block mt-1.5 text-[11px] font-medium ${d > todayISO() ? 'text-gray-400' : 'text-amber-700'}`}>
                            {d > todayISO() ? `Follow up ${fmtDay(d)}` : d < todayISO() ? `Overdue since ${fmtDay(d)}` : 'Follow up today'}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Person */}
        <div className={mobileDetail ? '' : 'hidden lg:block'}>
          {selected ? (
            <PersonCard key={selected.id} card={selected} token={token} headers={headers}
              onPatch={(b) => patch(selected, b)} onNext={goNext} onBack={() => setMobileDetail(false)} reload={load} />
          ) : (
            <div className="card p-10 text-center text-sm text-gray-500">Pick someone from the list.</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── One person ────────────────────────────────────────────────────────

const PersonCard: React.FC<{
  card: Card; token: string | null; headers: Record<string, string>;
  onPatch: (b: Record<string, any>) => void; onNext: () => void; onBack: () => void; reload: () => Promise<Card[] | null>;
}> = ({ card, token, headers, onPatch, onNext, onBack, reload }) => {
  const [subject, setSubject] = useState(card.email_subject || '');
  const [body, setBody] = useState(card.email_body || '');
  const [note, setNote] = useState(card.linkedin_note || '');
  const [opener, setOpener] = useState(card.call_opener || '');
  const [copied, setCopied] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<null | 'email' | 'linkedin' | 'call'>(null);
  const [finding, setFinding] = useState<null | 'email' | 'phone'>(null);
  const [findMsg, setFindMsg] = useState<string | null>(null);
  const [showDate, setShowDate] = useState(false);
  const isPerson = card.entity_type !== 'company';
  const email = card.entity_email || card.entity_personal_email;

  const saveIfChanged = (field: string, value: string, original: string | null) => {
    if ((value || '') !== (original || '')) onPatch({ [field]: value, log: { action: 'edited' } });
  };
  const copy = async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(what); setTimeout(() => setCopied(null), 2000); return true; }
    catch { return false; }
  };

  const openEmail = (via: 'gmail' | 'outlook' | 'app') => {
    const to = email || '';
    const s = encodeURIComponent(subject);
    const b = encodeURIComponent(body);
    const url = via === 'gmail' ? `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${s}&body=${b}`
      : via === 'outlook' ? `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(to)}&subject=${s}&body=${b}`
        : `mailto:${encodeURIComponent(to)}?subject=${s}&body=${b}`;
    if (via === 'app') window.location.href = url; else window.open(url, '_blank', 'noopener');
    onPatch({ log: { action: 'emailed', channel: 'email' } });
    setPrompt('email');
  };
  const sendLinkedIn = async () => {
    await copy(note, 'note');
    if (card.entity_linkedin) window.open(card.entity_linkedin, '_blank', 'noopener');
    onPatch({ log: { action: 'linkedin', channel: 'linkedin' } });
    setPrompt('linkedin');
  };

  // Get the email / mobile through the app's usual lookup (credits), then
  // check back until it lands (most providers answer within a minute).
  const find = async (what: 'email' | 'phone') => {
    setFinding(what); setFindMsg(null);
    try {
      const path = card.entity_type === 'candidate' ? 'candidate' : 'manager';
      const r = await fetch(`/api/fullenrich/enrich/${path}/${card.entity_id}?type=${what === 'email' ? 'work_email' : 'phone'}`, { method: 'POST', headers });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Couldn’t start the search');
      for (let i = 0; i < 12; i += 1) {
        await new Promise((res) => setTimeout(res, i === 0 ? 2500 : 5000));
        const list = await reload();
        const me = list && list.find((c) => c.id === card.id);
        if (me && (what === 'email' ? (me.entity_email || me.entity_personal_email) : me.entity_phone)) { setFinding(null); return; }
      }
      setFindMsg(what === 'email' ? 'No email found yet. You weren’t charged if nothing was found.' : 'No mobile found yet. You weren’t charged if nothing was found.');
    } catch (e: any) { setFindMsg(e.message || 'Couldn’t search'); }
    setFinding(null);
  };

  const mark = (status: Status, extra: Record<string, any> = {}) => { onPatch({ status, log: { action: 'status', note: STATUS[status].label }, ...extra }); setPrompt(null); };
  const followUp = (iso: string | null) => { onPatch({ due: iso, log: { action: 'snoozed', note: iso ? `Follow up ${fmtDay(iso)}` : 'Follow-up cleared' } }); setShowDate(false); };

  const st = STATUS[card.outreach_status] || STATUS.to_do;
  const evidence = evidenceOf(card);
  const noteLen = note.length;

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-gray-100">
        <button onClick={onBack} className="lg:hidden text-sm text-gray-500 mb-3 inline-flex items-center gap-1">← All people</button>
        <div className="flex items-start gap-4">
          <span className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center text-base font-semibold flex-shrink-0">{initials(card.entity_name)}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-semibold text-gray-900 tracking-tight">{card.entity_name || 'Unknown'}</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.pill}`}>{st.label}</span>
            </div>
            <p className="text-sm text-gray-600 mt-0.5">{[card.entity_title, card.entity_company].filter(Boolean).join(' · ')}</p>
            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-3 flex-wrap">
              {card.entity_location && <span>{card.entity_location}</span>}
              {card.entity_linkedin && <a href={card.entity_linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">LinkedIn profile ↗</a>}
              {card.stage_name && <span>Stage: {card.stage_name}</span>}
            </p>
          </div>
        </div>
        {(card.outreach_reason || card.outreach_about || evidence.length > 0) && (
          <div className="mt-4 rounded-xl bg-amber-50/70 border border-amber-100 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">Why now</p>
            {card.outreach_about && <p className="text-sm font-medium text-gray-900 mt-1">{card.outreach_about}</p>}
            {card.outreach_reason && <p className="text-sm text-gray-700 mt-0.5 leading-relaxed whitespace-pre-line">{card.outreach_reason}</p>}
            {evidence.length > 0 && (
              <div className="mt-3 pt-3 border-t border-amber-200/60">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800 mb-1.5">The evidence</p>
                <ul className="space-y-1.5">
                  {evidence.map((e, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-snug">
                      <span className="w-20 flex-shrink-0 text-xs text-gray-500 tabular-nums pt-0.5">{e.date || ''}</span>
                      <span className="min-w-0 text-gray-800">
                        {e.fact}
                        {(e.source || e.url) && (
                          <span className="ml-1.5 text-xs text-gray-500 whitespace-nowrap">
                            {e.url
                              ? <a href={e.url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">{e.source || hostOf(e.url)} ↗</a>
                              : e.source}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Contact details */}
      {isPerson && (
        <div className="px-6 py-4 border-b border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ContactLine label="Email" value={email} copied={copied === 'email'} onCopy={() => email && copy(email, 'email')}
            finding={finding === 'email'} onFind={() => find('email')} findLabel="Get email · 1 credit" />
          <ContactLine label="Mobile" value={card.entity_phone} copied={copied === 'phone'} onCopy={() => card.entity_phone && copy(card.entity_phone, 'phone')}
            finding={finding === 'phone'} onFind={() => find('phone')} findLabel="Get mobile · 12 credits" tel />
          {findMsg && <p className="sm:col-span-2 text-xs text-gray-500">{findMsg}</p>}
        </div>
      )}

      {/* "Did you send it?" */}
      {prompt && (
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-sm text-blue-900">
            {prompt === 'email' ? 'Sent the email?' : prompt === 'linkedin' ? 'Note copied and profile opened. Sent the request?' : 'How did the call go?'}
          </span>
          <div className="flex items-center gap-2">
            {prompt === 'call' ? (<>
              <button onClick={() => mark('contacted', { due: plusDays(2) })} className="btn-secondary !py-1.5 text-xs">No answer · try in 2 days</button>
              <button onClick={() => mark('replied')} className="btn-secondary !py-1.5 text-xs">Spoke · interested</button>
              <button onClick={() => mark('not_interested')} className="btn-secondary !py-1.5 text-xs">Not interested</button>
            </>) : (<>
              <button onClick={() => mark('contacted', { due: plusDays(4) })} className="btn-primary !py-1.5 text-xs">Yes · follow up in 4 days</button>
              <button onClick={() => setPrompt(null)} className="text-xs text-blue-800 hover:underline">Not yet</button>
            </>)}
          </div>
        </div>
      )}

      {/* Drafts */}
      <div className="px-6 py-5 space-y-6">
        <section>
          <SectionTitle icon="email" title="Email" hint={card.outreach_source === 'claude' ? 'Drafted by Claude · edit freely' : undefined} />
          <input className="input mt-2 font-medium" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)}
            onBlur={() => saveIfChanged('emailSubject', subject, card.email_subject)} />
          <AutoTextarea minRows={8} className="input mt-2 leading-relaxed" placeholder={`Hi ${firstName(card.entity_name)},`} value={body}
            onChange={(e) => setBody(e.target.value)} onBlur={() => saveIfChanged('emailBody', body, card.email_body)} />
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button onClick={() => openEmail('gmail')} disabled={!body.trim()} className="btn-primary !py-1.5 text-sm disabled:opacity-40">Open in Gmail</button>
            <button onClick={() => openEmail('outlook')} disabled={!body.trim()} className="btn-secondary !py-1.5 text-sm disabled:opacity-40">Open in Outlook</button>
            <button onClick={() => openEmail('app')} disabled={!body.trim()} className="btn-secondary !py-1.5 text-sm disabled:opacity-40">Mail app</button>
            <button onClick={() => copy(`${subject}\n\n${body}`, 'emailtext')} disabled={!body.trim()} className="btn-ghost !py-1.5 text-sm disabled:opacity-40">{copied === 'emailtext' ? 'Copied' : 'Copy'}</button>
          </div>
          {!email && body.trim() && <p className="text-xs text-gray-400 mt-2">No email address yet: get it above, or the email opens without one for you to fill in.</p>}
        </section>

        <section>
          <SectionTitle icon="linkedin" title="LinkedIn connection note" hint={`${noteLen}/300`} hintWarn={noteLen > 300} />
          <AutoTextarea minRows={3} className="input mt-2 leading-relaxed" placeholder="A short note to send with the connection request" value={note}
            onChange={(e) => setNote(e.target.value)} onBlur={() => saveIfChanged('linkedinNote', note, card.linkedin_note)} />
          <div className="flex items-center gap-2 mt-3">
            <button onClick={sendLinkedIn} disabled={!card.entity_linkedin} className="btn-secondary !py-1.5 text-sm disabled:opacity-40">
              {copied === 'note' ? 'Copied · opening LinkedIn' : 'Copy note & open profile'}
            </button>
          </div>
        </section>

        <section>
          <SectionTitle icon="call" title="Call" />
          <AutoTextarea minRows={2} className="input mt-2 leading-relaxed" placeholder="What to say in the first 20 seconds" value={opener}
            onChange={(e) => setOpener(e.target.value)} onBlur={() => saveIfChanged('callOpener', opener, card.call_opener)} />
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {card.entity_phone && <a href={`tel:${card.entity_phone.replace(/\s+/g, '')}`} className="btn-secondary !py-1.5 text-sm">Call {card.entity_phone}</a>}
            <button onClick={() => { onPatch({ log: { action: 'called', channel: 'call' } }); setPrompt('call'); }} className="btn-ghost !py-1.5 text-sm">Log a call</button>
          </div>
        </section>
      </div>

      {/* Outcome + next */}
      <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 mr-1">Mark as</span>
          {(['contacted', 'replied', 'meeting', 'not_interested'] as Status[]).map((s) => (
            <button key={s} onClick={() => mark(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${card.outreach_status === s ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`}>
              {STATUS[s].label}
            </button>
          ))}
          {card.outreach_status !== 'to_do' && <button onClick={() => mark('to_do')} className="text-xs text-gray-500 hover:text-gray-800 underline ml-1">Back to To do</button>}
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap mt-3">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 mr-1">Follow up</span>
            {[{ l: 'Tomorrow', d: 1 }, { l: 'In 3 days', d: 3 }, { l: 'Next week', d: 7 }, { l: 'In 2 weeks', d: 14 }].map((o) => (
              <button key={o.d} onClick={() => followUp(plusDays(o.d))} className="px-2.5 py-1 rounded-md text-xs border border-gray-200 bg-white text-gray-700 hover:border-gray-300">{o.l}</button>
            ))}
            {showDate ? (
              <input type="date" className="input !w-auto !py-1 text-xs" min={todayISO()} onChange={(e) => e.target.value && followUp(e.target.value)} />
            ) : <button onClick={() => setShowDate(true)} className="text-xs text-gray-500 hover:text-gray-800 underline">Pick a date</button>}
            {dueDay(card) && <span className="text-xs text-gray-500">· set for {fmtDay(dueDay(card)!)} <button onClick={() => followUp(null)} className="underline">clear</button></span>}
          </div>
          <button onClick={onNext} className="btn-primary !py-1.5 text-sm">Next person →</button>
        </div>
      </div>

      {/* Activity */}
      {card.outreach_log && card.outreach_log.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-100">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Activity</p>
          <ol className="space-y-1.5">
            {[...card.outreach_log].reverse().slice(0, 8).map((e, i) => (
              <li key={i} className="text-xs text-gray-600 flex gap-3">
                <span className="text-gray-400 tabular-nums w-28 flex-shrink-0">{new Date(e.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                <span>{ACTION_LABEL[e.action] || e.action}{e.note ? `: ${e.note}` : ''}{e.by ? <span className="text-gray-400"> · {e.by}</span> : null}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

// A textarea that grows with its text, so a whole draft is readable at once.
const AutoTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement> & { minRows?: number }> = ({ minRows = 3, className, ...props }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [props.value]);
  return <textarea ref={ref} rows={minRows} className={`${className || ''} resize-none overflow-hidden`} {...props} />;
};

const ICONS: Record<string, React.ReactNode> = {
  email: <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l9 6 9-6M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />,
  linkedin: <path strokeLinecap="round" strokeLinejoin="round" d="M8 10v7M8 7v.01M12 17v-4a2 2 0 014 0v4M12 10v7M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" />,
  call: <path strokeLinecap="round" strokeLinejoin="round" d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z" />,
};

const SectionTitle: React.FC<{ icon: string; title: string; hint?: string; hintWarn?: boolean }> = ({ icon, title, hint, hintWarn }) => (
  <div className="flex items-center justify-between gap-3">
    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
      <span className="w-7 h-7 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center" aria-hidden>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">{ICONS[icon]}</svg>
      </span>
      {title}
    </h3>
    {hint && <span className={`text-xs tabular-nums ${hintWarn ? 'text-red-600 font-medium' : 'text-gray-400'}`}>{hint}</span>}
  </div>
);

const ContactLine: React.FC<{
  label: string; value: string | null; copied: boolean; onCopy: () => void; finding: boolean; onFind: () => void; findLabel: string; tel?: boolean;
}> = ({ label, value, copied, onCopy, finding, onFind, findLabel, tel }) => (
  <div className="rounded-xl border border-gray-200 px-3.5 py-2.5 flex items-center justify-between gap-3 min-w-0">
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      {value ? (
        tel ? <a href={`tel:${value.replace(/\s+/g, '')}`} className="text-sm text-gray-900 font-medium truncate block hover:text-blue-700">{value}</a>
          : <p className="text-sm text-gray-900 font-medium truncate select-all">{value}</p>
      ) : <p className="text-sm text-gray-400">Not found yet</p>}
    </div>
    {value ? (
      <button onClick={onCopy} className="text-xs text-gray-500 hover:text-gray-900 flex-shrink-0">{copied ? 'Copied' : 'Copy'}</button>
    ) : (
      <button onClick={onFind} disabled={finding} className="text-xs font-medium text-blue-700 hover:text-blue-900 flex-shrink-0 disabled:opacity-60">
        {finding ? 'Searching…' : findLabel}
      </button>
    )}
  </div>
);

const EmptyState: React.FC = () => (
  <div className="card p-10 text-center max-w-2xl mx-auto">
    <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto text-xl" aria-hidden>✉</div>
    <h3 className="text-lg font-semibold text-gray-900 mt-4">Your outreach to-do list</h3>
    <p className="text-sm text-gray-500 mt-2 leading-relaxed max-w-[52ch] mx-auto">
      Ask Claude to find who to contact and draft the messages, then to save them to this campaign. Each person lands here with the
      reason to call, an email, a LinkedIn note and a call opener: get their details, send from your own mailbox, and log what happens.
    </p>
    <div className="mt-5 text-left bg-gray-50 border border-gray-100 rounded-xl p-4 text-sm text-gray-700 leading-relaxed">
      “Find the hiring managers at companies that lost a Mechanical Design Engineer this month, draft an email and a LinkedIn note for each,
      and save them to my campaign.”
    </div>
    <a href="/training?tab=claude" className="inline-block mt-4 text-sm font-medium text-blue-600 hover:underline">More prompts to copy →</a>
  </div>
);

export default CampaignOutreach;
